import "@dxos/node-std/globals"

// packages/common/util/src/binder.ts
import util from "@dxos/node-std/util";
var createBinder = (obj) => ({
  fn: (fn) => fn.bind(obj),
  async: (fn) => util.promisify(fn.bind(obj))
});

// packages/common/util/src/callback.ts
import assert from "@dxos/node-std/assert";
var Callback = class {
  call(...args) {
    assert(this._callback, "Callback not set");
    return this._callback(...args);
  }
  callIfSet(...args) {
    var _a;
    return (_a = this._callback) == null ? void 0 : _a.call(this, ...args);
  }
  set(callback) {
    assert(!this._callback, "Callback already set");
    this._callback = callback;
  }
  isSet() {
    return !!this._callback;
  }
};
var createSetDispatch = ({ handlers }) => {
  return new Proxy({
    handlers
  }, {
    get: (target, prop) => {
      return (...args) => {
        handlers.forEach((handler) => {
          const method = handler[prop];
          if (method) {
            method.apply(handler, args);
          }
        });
      };
    }
  });
};

// packages/common/util/src/complex.ts
import { inspect } from "@dxos/node-std/util";
import { inspectObject, raise } from "@dxos/debug";
var MAX_SERIALIZATION_LENGTH = 10;
var ComplexSet = class {
  // prettier-ignore
  constructor(_projection, values) {
    this._projection = _projection;
    this._values = /* @__PURE__ */ new Map();
    if (values) {
      for (const value of values) {
        this.add(value);
      }
    }
  }
  toString() {
    return inspectObject(this);
  }
  toJSON() {
    return this._values.size > MAX_SERIALIZATION_LENGTH ? {
      size: this._values.size
    } : Array.from(this._values.values());
  }
  [inspect.custom]() {
    return inspectObject(this);
  }
  add(value) {
    this._values.set(this._projection(value), value);
    return this;
  }
  clear() {
    this._values.clear();
  }
  delete(value) {
    return this._values.delete(this._projection(value));
  }
  forEach(callbackfn, thisArg) {
    if (thisArg) {
      callbackfn = callbackfn.bind(thisArg);
    }
    this._values.forEach((value) => callbackfn(value, value, this));
  }
  has(value) {
    return this._values.has(this._projection(value));
  }
  get size() {
    return this._values.size;
  }
  [Symbol.iterator]() {
    return this._values.values();
  }
  *entries() {
    for (const value of this._values.values()) {
      yield [
        value,
        value
      ];
    }
  }
  keys() {
    return this[Symbol.iterator]();
  }
  values() {
    return this[Symbol.iterator]();
  }
  get [Symbol.toStringTag]() {
    return "ComplexSet";
  }
};
var makeSet = (projection) => {
  return class BoundComplexSet extends ComplexSet {
    constructor(values) {
      super(projection, values);
    }
  };
};
var ComplexMap = class {
  // prettier-ignore
  constructor(_keyProjection, entries) {
    this._keyProjection = _keyProjection;
    this._keys = /* @__PURE__ */ new Map();
    this._values = /* @__PURE__ */ new Map();
    if (entries) {
      for (const [key, value] of entries) {
        this.set(key, value);
      }
    }
  }
  toString() {
    return inspectObject(this);
  }
  toJSON() {
    return this._values.size > MAX_SERIALIZATION_LENGTH ? {
      size: this._values.size
    } : Array.from(this._values.values());
  }
  [inspect.custom]() {
    return inspectObject(this);
  }
  clear() {
    this._keys.clear();
    this._values.clear();
  }
  delete(key) {
    const keyDeleted = this._keys.delete(this._keyProjection(key));
    const valueDeleted = this._values.delete(this._keyProjection(key));
    return keyDeleted || valueDeleted;
  }
  forEach(callbackfn, thisArg) {
    if (thisArg) {
      callbackfn = callbackfn.bind(thisArg);
    }
    this._keys.forEach((key, primitive) => {
      var _a;
      return callbackfn((_a = this._values.get(primitive)) != null ? _a : raise(new Error("Map corrupted.")), key, this);
    });
  }
  get(key) {
    return this._values.get(this._keyProjection(key));
  }
  has(key) {
    return this._keys.has(this._keyProjection(key));
  }
  set(key, value) {
    const primitive = this._keyProjection(key);
    this._keys.set(primitive, key);
    this._values.set(primitive, value);
    return this;
  }
  get size() {
    return this._keys.size;
  }
  *[Symbol.iterator]() {
    var _a;
    for (const [primitive, key] of this._keys) {
      const value = (_a = this._values.get(primitive)) != null ? _a : raise(new Error("Map corrupted."));
      yield [
        key,
        value
      ];
    }
  }
  entries() {
    return this[Symbol.iterator]();
  }
  keys() {
    return this._keys.values();
  }
  values() {
    return this._values.values();
  }
  get [Symbol.toStringTag]() {
    return "ComplexMap";
  }
};
var makeMap = (keyProjection) => class BoundComplexMap extends ComplexMap {
  constructor(entries) {
    super(keyProjection, entries);
  }
};

// packages/common/util/src/default-map.ts
var defaultMap = (map, def) => (key) => {
  let value = map.get(key);
  if (value === void 0) {
    value = def();
    map.set(key, value);
  }
  return value;
};

// packages/common/util/src/defer-function.ts
var deferFunction = (fnProvider) => (...args) => fnProvider()(...args);

// packages/common/util/src/entry.ts
var entry = (map, key) => new MapEntry(map, key);
var MapEntry = class {
  /**
  * @internal
  */
  constructor(_map, _key) {
    this._map = _map;
    this._key = _key;
  }
  get key() {
    return this._key;
  }
  get value() {
    return this._map.get(this._key);
  }
  orInsert(value) {
    if (!this._map.has(this._key)) {
      this._map.set(this._key, value);
    }
    return this;
  }
};

// packages/common/util/src/human-hash.ts
import { PublicKey } from "@dxos/keys";
var DEFAULT_WORDLIST = [
  "ack",
  "alabama",
  "alanine",
  "alaska",
  "alpha",
  "angel",
  "apart",
  "april",
  "arizona",
  "arkansas",
  "artist",
  "asparagus",
  "aspen",
  "august",
  "autumn",
  "avocado",
  "bacon",
  "bakerloo",
  "batman",
  "beer",
  "berlin",
  "beryllium",
  "black",
  "blossom",
  "blue",
  "bluebird",
  "bravo",
  "bulldog",
  "burger",
  "butter",
  "california",
  "carbon",
  "cardinal",
  "carolina",
  "carpet",
  "cat",
  "ceiling",
  "charlie",
  "chicken",
  "coffee",
  "cola",
  "cold",
  "colorado",
  "comet",
  "connecticut",
  "crazy",
  "cup",
  "dakota",
  "december",
  "delaware",
  "delta",
  "diet",
  "don",
  "double",
  "early",
  "earth",
  "east",
  "echo",
  "edward",
  "eight",
  "eighteen",
  "eleven",
  "emma",
  "enemy",
  "equal",
  "failed",
  "fanta",
  "fifteen",
  "fillet",
  "finch",
  "fish",
  "five",
  "fix",
  "floor",
  "florida",
  "football",
  "four",
  "fourteen",
  "foxtrot",
  "freddie",
  "friend",
  "fruit",
  "gee",
  "georgia",
  "glucose",
  "golf",
  "green",
  "grey",
  "hamper",
  "happy",
  "harry",
  "hawaii",
  "helium",
  "high",
  "hot",
  "hotel",
  "hydrogen",
  "idaho",
  "illinois",
  "india",
  "indigo",
  "ink",
  "iowa",
  "island",
  "item",
  "jersey",
  "jig",
  "johnny",
  "juliet",
  "july",
  "jupiter",
  "kansas",
  "kentucky",
  "kilo",
  "king",
  "kitten",
  "lactose",
  "lake",
  "lamp",
  "lemon",
  "leopard",
  "lima",
  "lion",
  "lithium",
  "london",
  "louisiana",
  "low",
  "magazine",
  "magnesium",
  "maine",
  "mango",
  "march",
  "mars",
  "maryland",
  "massachusetts",
  "may",
  "mexico",
  "michigan",
  "mike",
  "minnesota",
  "mirror",
  "mississippi",
  "missouri",
  "mobile",
  "mockingbird",
  "monkey",
  "montana",
  "moon",
  "mountain",
  "muppet",
  "music",
  "nebraska",
  "neptune",
  "network",
  "nevada",
  "nine",
  "nineteen",
  "nitrogen",
  "north",
  "november",
  "nuts",
  "october",
  "ohio",
  "oklahoma",
  "one",
  "orange",
  "oranges",
  "oregon",
  "oscar",
  "oven",
  "oxygen",
  "papa",
  "paris",
  "pasta",
  "pennsylvania",
  "pip",
  "pizza",
  "pluto",
  "potato",
  "princess",
  "purple",
  "quebec",
  "queen",
  "quiet",
  "red",
  "river",
  "robert",
  "robin",
  "romeo",
  "rugby",
  "sad",
  "salami",
  "saturn",
  "september",
  "seven",
  "seventeen",
  "shade",
  "sierra",
  "single",
  "sink",
  "six",
  "sixteen",
  "skylark",
  "snake",
  "social",
  "sodium",
  "solar",
  "south",
  "spaghetti",
  "speaker",
  "spring",
  "stairway",
  "steak",
  "stream",
  "summer",
  "sweet",
  "table",
  "tango",
  "ten",
  "tennessee",
  "tennis",
  "texas",
  "thirteen",
  "three",
  "timing",
  "triple",
  "twelve",
  "twenty",
  "two",
  "uncle",
  "undress",
  "uniform",
  "uranus",
  "utah",
  "vegan",
  "venus",
  "vermont",
  "victor",
  "video",
  "violet",
  "virginia",
  "washington",
  "west",
  "whiskey",
  "white",
  "william",
  "winner",
  "winter",
  "wisconsin",
  "wolfram",
  "wyoming",
  "xray",
  "yankee",
  "yellow",
  "zebra",
  "zulu"
];
var HumanHasher = class {
  /**
  * Transforms hex digests to human-readable strings.
  *
  * The format of these strings will look something like:
  * `victor-bacon-zulu-lima`. The output is obtained by compressing the input
  * digest to a fixed number of bytes, then mapping those bytes to one of 256
  * words. A default wordlist is provided, but you can override this if you
  * prefer.
  * As long as you use the same wordlist, the output will be consistent (i.e.
  * the same digest will always render the same representation).
  *
  * @param wordlist A list of exactly 256 words to choose from
  */
  constructor(wordlist = DEFAULT_WORDLIST) {
    this.wordlist = wordlist;
    if (wordlist.length !== 256) {
      throw new Error("Wordlist must have exactly 256 items");
    }
    this.wordlist = wordlist;
  }
  /**
  * Humanize a given hexadecimal digest.
  *
  * Change the number of words output by specifying `words`. Change the
  * word separator with `separator`.
  *
  * @param hexdigest A string of hexadecimal characters to humanize
  * @param words How many words to output (more = safer)
  * @param separator The string used to seperate the words
  */
  humanize(hexdigest, words = 4, separator = "-") {
    const pairs = hexdigest.match(/(..?)/g);
    if (!pairs) {
      throw new Error("");
    }
    const bytes = pairs.map((x) => parseInt(x, 16));
    const compressed = this._compress(bytes, words);
    return compressed.map((x) => this.wordlist[x]).join(separator);
  }
  /**
  * Compress a list of byte values to a fixed target length.
  *
  * @param bytes A list of bytes (numbers from 0-254)
  * @param target The number of bytes to return / compress to
  */
  _compress(bytes, target) {
    const length = bytes.length;
    if (target > length) {
      throw new Error("Fewer input bytes than requested output");
    }
    const segSize = length / target >> 0;
    const segments = [];
    for (let i = 0; i < segSize * target; i += segSize) {
      segments.push(bytes.slice(i, i + segSize));
    }
    segments[segments.length - 1] = segments[segments.length - 1].concat(bytes.slice(target * segSize));
    const checksums = segments.map((x) => x.reduce((acc, curr) => acc ^ curr));
    return checksums;
  }
};
var hasher = new HumanHasher();
var humanize = (value) => {
  if (value instanceof Buffer || value instanceof Uint8Array || value instanceof ArrayBuffer) {
    value = PublicKey.stringify(value);
  } else if (value instanceof PublicKey) {
    value = value.toHex();
  }
  return hasher.humanize(value);
};

// packages/common/util/src/interval.ts
var exponentialBackoffInterval = (cb, initialInterval) => {
  let interval = initialInterval;
  const repeat = () => {
    cb();
    interval *= 2;
    timeoutId = setTimeout(repeat, interval);
  };
  let timeoutId = setTimeout(repeat, interval);
  return () => clearTimeout(timeoutId);
};

// packages/common/util/src/json.ts
import { inspect as inspect2 } from "@dxos/node-std/util";
import { PublicKey as PublicKey2 } from "@dxos/keys";
function jsonReplacer(key, value) {
  if (value !== null && typeof value === "object" && typeof value[inspect2.custom] === "function") {
    return value[inspect2.custom]();
  }
  if (value !== null && typeof value === "object" && value.type === "Buffer" && Array.isArray(value.data)) {
    if (value.data.length === 32) {
      const key1 = Buffer.from(value.data);
      return `[${humanize(key1)}]:[${PublicKey2.stringify(key1)}]`;
    } else {
      return Buffer.from(value.data).toString("hex");
    }
  }
  return value;
}

// packages/common/util/src/map.ts
import assert2 from "@dxos/node-std/assert";
var LazyMap = class extends Map {
  constructor(_initFn) {
    super();
    this._initFn = _initFn;
  }
  getOrInit(key) {
    assert2(key);
    if (this.has(key)) {
      return this.get(key);
    } else {
      const value = this._initFn(key);
      this.set(key, value);
      return value;
    }
  }
};

// packages/common/util/src/platform.ts
var isNode = () => typeof process !== "undefined" && process.versions != null && process.versions.node != null;

// packages/common/util/src/random.ts
var randomInt = (max, min) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// packages/common/util/src/range.ts
var range = (n) => Array.from(Array(n).keys());

// packages/common/util/src/safe-instanceof.ts
var instanceTag = Symbol("instanceTag");
var safeInstanceof = (tag) => (target) => {
  target.prototype[instanceTag] = tag;
  Object.defineProperty(target.prototype, Symbol.hasInstance, {
    value: (instance) => instance[instanceTag] === tag
  });
};

// packages/common/util/src/sort.ts
var alphabetical = (direction = 1) => {
  return (v1, v2) => {
    const a = v1 == null ? void 0 : v1.toLowerCase();
    const b = v2 == null ? void 0 : v2.toLowerCase();
    return a < b ? direction * -1 : a > b ? direction : 0;
  };
};
var alphabeticalByKey = (key, direction = 1) => {
  return (v1, v2) => {
    var _a, _b;
    const a = (_a = v1[key]) == null ? void 0 : _a.toLowerCase();
    const b = (_b = v2[key]) == null ? void 0 : _b.toLowerCase();
    return a < b ? direction * -1 : a > b ? direction : 0;
  };
};

// packages/common/util/src/types.ts
var boolGuard = (value) => Boolean(value);
var isNotNullOrUndefined = (x) => x != null;
var getAsyncValue = async (value) => {
  if (typeof value === "function") {
    return value();
  } else {
    return value;
  }
};
var stripKeys = (obj) => {
  if (typeof obj === "object") {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      if (value === void 0) {
        delete obj[key];
      } else if (value !== null && typeof value === "object") {
        stripKeys(value);
      }
    });
  }
  return obj;
};

// packages/common/util/src/uint8array.ts
var arraysEqual = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};
export {
  Callback,
  ComplexMap,
  ComplexSet,
  HumanHasher,
  LazyMap,
  MapEntry,
  alphabetical,
  alphabeticalByKey,
  arraysEqual,
  boolGuard,
  createBinder,
  createSetDispatch,
  defaultMap,
  deferFunction,
  entry,
  exponentialBackoffInterval,
  getAsyncValue,
  humanize,
  isNode,
  isNotNullOrUndefined,
  jsonReplacer,
  makeMap,
  makeSet,
  randomInt,
  range,
  safeInstanceof,
  stripKeys
};
//# sourceMappingURL=index.mjs.map
